import React from 'react';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business'; // Using Business for Townhouse
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Using Help for Other
import HouseIcon from '@mui/icons-material/House';
import { Box } from '@mui/material';
import { Typography } from '@rentspree/component-2023.components.atoms.typography';
import { Card } from '@rentspree/component-2023.components.organisms.card';

interface PropertyType {
  id: string;
  label: string;
  description: string;
  icon: React.ReactElement;
}

const propertyTypes: PropertyType[] = [
  {
    id: 'apartment',
    label: 'Apartment',
    description: 'Multi-unit building',
    icon: <ApartmentIcon sx={{ fontSize: 40 }} />,
  },
  {
    id: 'house',
    label: 'House',
    description: 'Single-family home',
    icon: <HouseIcon sx={{ fontSize: 40 }} />,
  },
  {
    id: 'townhouse',
    label: 'Townhouse',
    description: 'Multi-level attached',
    icon: <BusinessIcon sx={{ fontSize: 40 }} />,
  },
  {
    id: 'other',
    label: 'Other',
    description: 'e.g. Condo, Loft',
    icon: <HelpOutlineIcon sx={{ fontSize: 40 }} />,
  },
];

interface PropertyTypeSelectorProps {
  selectedType: string | null;
  onSelectType: (type: string) => void;
}

export const PropertyTypeSelector: React.FC<PropertyTypeSelectorProps> = ({
  selectedType,
  onSelectType,
}) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)', // 2 columns on mobile
          sm: 'repeat(2, 1fr)', // 2 columns on small tablets
          md: 'repeat(4, 1fr)', // 4 columns on desktop
        },
        gap: { xs: 2, md: 2.5 },
        justifyContent: 'center',
        width: '100%',
      }}
    >
      {propertyTypes.map(type => (
        <Card
          key={type.id}
          variant={selectedType === type.id ? 'elevation' : 'outlined'}
          onClick={() => onSelectType(type.id)}
          sx={{
            height: 200,
            padding: 3,
            cursor: 'pointer',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            border: theme =>
              selectedType === type.id
                ? `2px solid ${theme.palette.grey[900]}`
                : `2px solid ${theme.palette.grey[300]}`,
            backgroundColor:
              selectedType === type.id ? 'rgba(31,21,225,0.05)' : '#ffffff',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '12px',
              backgroundColor: theme =>
                selectedType === type.id
                  ? 'rgba(31,21,225,0.1)'
                  : theme.palette.grey[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {type.icon}
          </Box>
          <Typography variant='subtitle1' sx={{ fontWeight: '600' }}>
            {type.label}
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {type.description}
          </Typography>
        </Card>
      ))}
    </Box>
  );
};
